/**
 * Parcels Sender
 */

// React
import React, { FunctionComponent, useEffect, useState, useContext } from 'react';

// Context
import AuthContext from '../../authContext';

// Components
import Pagination from '@material-ui/lab/Pagination';
import List from './components/list';
import Add from './components/Add/add';
import Edit from './components/Edit/edit';
import Modal from '../../components/Modal';
import Can from '../../components/Can';

import RenderFilter from '../../utils/listDaynamicFilterHookForm';

// UI
import theme from '../../theme';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  DialogTitle,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Divider,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiWarehouse,
  mdiClose,
  mdiCalendar,
  mdiKeyOutline,
  mdiStateMachine,
  mdiPackageVariantClosed,
  mdiPlus,
  mdiPencilOutline,
  mdiText,
} from '@mdi/js';

// Helper
import {
  removeEmptyValueObject,
  translate,
} from '../../utils/helpers';
import useQuery from '../../utils/hook/useQuery';

// Type
import {
  Props,
  Status,
} from './interfaces';
import type { Filter } from '../../utils/listDaynamicFilter';
import jMoment from 'moment-jalaali';
import CSV from './csv';
import { Link } from 'react-router-dom';
import handleError from '../../utils/handleError';
import handleNotfication from '../../utils/handleNotification';
import parcelsRepository from 'client-sdk/src/Repository/parcels-repository';

type Inputs = {
  'id.contains': string;
};

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    pagination: {
      // marginTop: theme.spacing(1),
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: '10px 0',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    errorText: {
      paddingTop: '1em',
      fontSize: '0.875rem',
      textAlign: 'left',
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      marginRight: '10px',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '30px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },

    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  })
);

const Parcels: FunctionComponent<Props> = (props: Props) => {
  const authContext = useContext(AuthContext);
  const [openFilterBox, setOpenFilterBox] = useState(false as boolean);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [disableCancelParcelModal, setDisableCancelParcelModal] = useState(
    false as boolean
  );
  const [inputError, setInputError] = useState(false as boolean);

  const { filtersValues, setFiltersValues } = useQuery({
    page: 0,
    size: 10,
    "parcelType.equals": "RECEIPT"
  } as any);
  const [filters, setFilters] = useState([] as Filter[]);
  const [openConfirmDisable, setOpenConfirmDisable] = useState(
    false as boolean
  );
  const [openDialog, setOpenDialog] = useState(false as boolean);
  const [openAdd, setOpenAdd] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [openSize, setOpenSize] = React.useState(false as boolean);
  const [openConfirmUpdateState, setOpenConfirmUpdateState] = useState(
    false as boolean
  );
  const [previewState, setPreviewState] = useState('CANCELLED' as Status);

  const [parcelsList, setParcelsList] = useState([]);
  const [allParcels, setAllParcels] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const filterTranslator = (fieldName: string) => {
    const status = {
      customerName: 'نام سفارش‌دهنده:',
      'customerName.equals': 'نام سفارش‌دهنده:',
      'customerName.contais': 'نام سفارش‌دهنده:',
      centerName: 'انبار:',
      'centerName.equals': 'انبار:',
      'centerName.contains': 'انبار:',
      id: 'شناسه مرسوله:',
      'id.equals': 'شناسه مرسوله:',
      'id.contains': 'شناسه مرسوله:',
      orderId: 'شناسه سفارش:',
      'orderId.equals': 'شناسه سفارش:',
      'orderId.contains': 'شناسه سفارش:',
      deliveryDate: 'تاریخ تحویل',
      'deliveryDate.equals': 'تاریخ تحویل:',
      'deliveryDate.greaterThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.greaterThan': 'تاریخ تحویل:',
      'deliveryDate.lessThanOrEqual': 'تاریخ تحویل:',
      'deliveryDate.lessThan': 'تاریخ تحویل:',
      packagedDate: 'تاریخ تحویل به لجستیک:',
      'packagedDate.equals': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.greaterThan': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThanOrEqual': 'تاریخ تحویل به لجستیک:',
      'packagedDate.lessThan': 'تاریخ تحویل به لجستیک:',
      status: 'وضعیت سفارش:',
      'status.in': 'وضعیت سفارش:',
      'status.notIn': 'وضعیت سفارش:',
      partnerName: 'شرکت:',
      'partnerName.equals': 'شرکت:',
      'partnerName.contains': 'شرکت:',
      weight: 'وزن:',
      'weight.equals': 'وزن:',
      'weight.contains': 'وزن:',
      length: 'ابعاد:',
      'length.equals': 'ابعاد:',
      'length.contains': 'ابعاد:',
    };
    return status[fieldName];
  };
  const removeFilterHandler = (fieldName: any) => {
    const filtered = { ...filtersValues };
    delete filtered[fieldName];
    setFiltersValues(filtered);
  };

  useEffect(() => {
    getParcels();
    setupFilters();
  }, [filtersValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useStyles();
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };
  const getParcel = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response, count } = await parcelsRepository.getParcels({ payload });
      setParcelsList(response.data);
      setCount(count.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const createParcels = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { pagination, callback, id } = await parcelsRepository.createParcel({
        payload,
      });
      handleNotfication('محموله با موفقیت اضافه شد', 'success');
      callback && callback();
    } catch (error) {
      setError(error.message);
      handleNotfication('اضافه کردن محصول با خطا مواجه شد', 'error');
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const getParcels = () => {
    const { page, size } = filtersValues;
    const { warehouseId } = authContext.user;
    const { partnerId } = authContext.user;

    getParcel({
      ...removeEmptyValueObject(filtersValues),
      partnerId: partnerId ? { equals: partnerId } : undefined, // partner_id => partner_admin role
      sourceWarehouseId: warehouseId ? { equals: warehouseId } : undefined, // WHId
      page,
      size,
    });
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({ ...filtersValues, page: 0, size: event.target.value });
  };

  const handleOpen = (id: number, type: string) => {
    // setType(type);
    setOpenDialog(true);
    setId(id);
  };

  const handleClose = () => {
    setOpenConfirmDisable(false);
    setOpenDialog(false);
    setOpenAdd(false);
    setOpenSize(false);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: any }) => {
    setId(data.id);
    setOpenConfirmUpdateState(true);
    setPreviewState(data.body);
  };

  const onSubmit = (filterValues: any) => {
    setFiltersValues({
      ...removeEmptyValueObject(filterValues),
      page: 0,
      size: 10,
    });
    setOpenFilterBox(false);
  };

  const handleClickAway = (e: any) => {
    // TODO: Sheet
    if (
      e.target.getAttribute('dir') !== 'rtl' &&
      e.target.getAttribute('type') !== 'checkbox' &&
      e.target.getAttribute('class') !== 'PrivateSwitchBase-input-73' &&
      e.target.getAttribute('class') !==
      'MuiTypography-root MuiListItemText-primary MuiTypography-body1 MuiTypography-displayBlock' &&
      e.target.getAttribute('aria-hidden') !== 'true'
    )
      setOpenFilterBox(false);
  };

  const handleClick = () => {
    setOpenFilterBox(prev => !prev);
  };

  const handleDisableCancelModal = () => {
    setDisableCancelParcelModal(false);
  };

  const setupFilters = () => {
    setFilters([
      {
        type: 'input',
        id: 'destinationWarehouseName.contains',
        label: 'انبار مقصد',
        icon: mdiWarehouse,
      },
      {
        type: 'input',
        id: 'receiptTitle.contains',
        label: 'عنوان قبض',
        icon: mdiText,
      },
      {
        type: 'input',
        id: 'id.equals',
        label: 'شناسه محموله',
        icon: mdiKeyOutline,
      },
      {
        type: 'date-picker',
        id: 'shippingDate.greaterThanOrEqual',
        label: 'تاریخ ثبت محموله از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'shippingDate.lessThanOrEqual',
        label: 'تا تاریخ ثبت محموله ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.greaterThanOrEqual',
        label: 'تاریخ تحویل از ',
        icon: mdiCalendar,
      },
      {
        type: 'date-picker',
        id: 'deliveryDate.lessThanOrEqual',
        label: 'تا تاریخ تحویل',
        icon: mdiCalendar,
      },
      {
        type: 'checkbox',
        id: 'status.in',
        label: 'وضعیت محموله',
        data: ['SUBMITTED', 'SHIPPED', 'DELIVERED', 'CANCELLED'],
        translateType: 'ParcelsStatus',
        icon: mdiStateMachine,
      },
    ]);
  };
  const headCells = [
    {
      id: 'destinationWarehouseName',
      label: 'انبار مقصد',
      type: 'input',
    },
    {
      id: 'partnerName',
      label: 'نام شرکت',
      type: 'input',
    },
    {
      id: 'receiptTitle',
      label: 'عنوان قبض',
      type: 'input',
    },
    {
      id: 'id',
      label: 'شناسه محموله',
      type: 'input',
    },
    {
      id: 'shippingDate',
      label: 'تاریخ ثبت',
      type: 'date-picker',
    },
    {
      id: 'deliveryDate',
      label: 'تاریخ تحویل',
      type: 'date-picker',
    },
    {
      id: 'deliveryTime',
      label: 'بازه تحویل',
      type: 'date-picker',
    },
    {
      id: 'status',
      label: 'وضعیت',
      data: ['SUBMITTED', 'SHIPPED', 'DELIVERED', 'CANCELLED'],
      translateType: 'ParcelsStatus',
      type: 'checkbox',
    },
  ];
  const head = { headCells };
  const getAllParcels = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const response = await parcelsRepository.getAllParcelsReport({ payload });
      setAllParcels(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const updateParcels = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { pagination, callback } = await parcelsRepository.updateParcels({
        payload,
      });
      handleNotfication('محموله مورد نظر با موفقیت ویرایش شد ', 'success');
      callback && callback();
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const cancelParcel = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const response = await parcelsRepository.cancelParcel({ payload });
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    try {
      if (validateRequiredInput(cancelReason)) {
        await cancelParcel({
          id,
          body: cancelReason,
        });
        setOpenConfirmDisable(false);
        setCancelReason('');
      }
    } catch (err) { }
  };

  const validateParcelStatus = (id: number) => {
    const parcel = parcelsList.find(item => item.id === id);
    return !!(parcel && parcel.status === 'SUBMITTED');
  };

  const handleCancelParcelReason = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (validateRequiredInput(target.value)) {
      setCancelReason(target.value.trim());
    }
  };

  const validateRequiredInput = (value: string) => {
    if (value.trim().length > 0) {
      if (inputError) {
        setInputError(false);
      }
      return true;
    } else {
      setInputError(true);
      return false;
    }
  };

  const handleOpenConfirmDisable = (id: number) => {
    setId(id);
    if (validateParcelStatus(id)) {
      setOpenConfirmDisable(true);
    } else {
      setDisableCancelParcelModal(true);
    }
  };

  const updateParcelsStatus = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const callback = await parcelsRepository.updateParcelStatus({ payload });
      handleNotfication('محموله با موفقیت به روز رسانی شد', 'success');
      callback && callback();
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };

  const changeStatusParcel = () => {
    const { warehouseId } = authContext.user;
    const params = warehouseId
      ? {
        sourceWarehouseId: { equals: warehouseId },
      }
      : { ...filtersValues };

    updateParcelsStatus({
      id,
      body: { status: previewState, description: '' },
      pagination: params,
    });
    setOpenConfirmUpdateState(false);
  };

  const findStatus = (id: number) => {
    const status = parcelsList.find(parcel => parcel.id === id)
      ?.status as Status;
    return status && translate(status, 'parcel');
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={6}>
        <Grid container item xs={12} sm={6} alignItems="center">
          <Typography variant="h4" component="h2">
            {'ارسال محموله‌'}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <RenderFilter
            filters={filters}
            onFilter={(filterValues: any) => {
              if (filterValues['deliveryStartTime.greaterThanOrEqual']) {
                filterValues['deliveryStartTime.greaterThanOrEqual'] = jMoment(
                  filterValues['deliveryStartTime.greaterThanOrEqual']
                )
                  .locale('en')
                  .format('HH:mm:ss');
              }
              if (filterValues['deliveryEndTime.lessThanOrEqual']) {
                filterValues['deliveryEndTime.lessThanOrEqual'] = jMoment(
                  filterValues['deliveryEndTime.lessThanOrEqual']
                )
                  .locale('en')
                  .format('HH:mm:ss');
              }
              setFiltersValues({
                ...removeEmptyValueObject(filterValues),
                page: 0,
                size: 10,
              });
            }}
            mainFiltersCount={0}
            baseFilter={{
              type: 'input',
              id: 'id.equals',
              placeholder: 'شناسه محموله',
            }}
            filterValues={filtersValues}
          />
        </Grid>
        <Grid container item xs={12} direction="row-reverse">
          <Can
            role={authContext.user.role}
            perform="parcels:addParcel"
            yes={() => (
              <>
                <Link
                  to="/parcels-exit/add"
                  style={{ marginLeft: '10px', textDecoration: 'none' }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<Icon path={mdiPlus} size={1} />}
                  >
                    {'ایجاد محموله جدید'}
                  </Button>
                </Link>
              </>
            )}
          />
        </Grid>
      </Grid>
      <Grid style={{ marginBottom: '10px' }} container xs={12} justify="flex-end">
        <Can
          role={authContext.user.role}
          perform="parcels:createNewParcel"
          yes={() => (
            <Link
              to="/parcels-sender/add"
              style={{ marginLeft: '10px', textDecoration: 'none' }}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Icon path={mdiPlus} size={1} />}
              >
                {'ایجاد محموله جدید'}
              </Button>
            </Link>
          )}
        />{' '}
        <Can
          role={authContext.user.role}
          perform="parcels:downloadReportCSV"
          yes={() => (
            <>
              <CSV
                parcelsDispatch={getAllParcels}
                data={allParcels}
                filtersValues={filtersValues}
                loading={loading}
              />
            </>
          )}
        />
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {parcelsList.length > 0 ? (
                <List
                  data={parcelsList}
                  handleOpenConfirmDisable={handleOpenConfirmDisable}
                  handleOpen={handleOpen}
                  changeStatus={handleOpenConfirmUpdateState}
                  head={head}
                  filterVal={filtersValues}
                  onFilterValue={(filtervalues: any) => {
                    setFiltersValues({
                      ...removeEmptyValueObject(filtervalues),
                      ...filtersValues,
                      page: 0,
                      size: 10,
                    });
                  }}
                  removeFilterHandler={removeFilterHandler}
                  filterLabelTranslator={filterTranslator}
                />
              ) : (
                <Typography variant="h3" component="div">
                  محموله موجود نیست
                </Typography>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {parcelsList.length > 0 && (
            <FormControl
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                {' '}
                تعداد نمایش در هر صفحه:
              </div>
              <Select
                open={openSize}
                onClose={handleClose}
                onOpen={() => setOpenSize(true)}
                value={filtersValues.size}
                onChange={handleChangeSizePage}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid xs={12} sm={6} container item direction="row-reverse">
          {parcelsList.length > 0 && (
            <Pagination
              className={classes.pagination}
              count={Math.ceil(count / filtersValues.size)}
              color="secondary"
              page={filtersValues.page + 1}
              onChange={handleChangePage}
            />
          )}
        </Grid>
      </Grid>

      <Modal
        maxWidth="lg"
        fullScreen
        onClose={handleClose}
        open={openAdd}
        title={
          <div className={classes.addIcon}>
            <Icon
              path={mdiPackageVariantClosed}
              size={4}
              color={theme.palette.green.main}
            />
          </div>
        }
        content={
          <Add
            warehouseId={props.match.params.id}
            onClose={handleClose}
            create={body => {
              createParcels({
                body,
                pagination: {
                  ...filtersValues,
                  sourceWarehouseId: { equals: authContext.user?.warehouseId },
                },
              });
              handleClose();
            }}
          />
        }
      />

      <Modal
        maxWidth="lg"
        fullScreen
        onClose={handleClose}
        open={openDialog}
        title={
          <div className={classes.addIcon}>
            <Icon
              path={mdiPackageVariantClosed}
              size={4}
              color={theme.palette.green.main}
            />
          </div>
        }
        content={
          <Edit
            warehouseId={props.match.params.id}
            data={parcelsList?.find(item => item.id === id)}
            onClose={handleClose}
            create={body => {
              updateParcels({
                body,
                pagination: {
                  ...filtersValues,
                  sourceWarehouseId: { equals: authContext.user?.warehouseId },
                },
                id: props.match.params.id,
              });
              handleClose();
            }}
          />
        }
      />

      <Dialog
        fullWidth
        open={openConfirmDisable}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            <IconButton aria-label="close" onClick={handleClose}>
              <Icon path={mdiClose} size={1} />
            </IconButton>
          </div>
          <div className={classes.cancelIcon}>
            <Icon path={mdiClose} size={4} color={theme.palette.error.main} />
          </div>
          <div className={classes.cancelTitle}>
            {` از 
            لغو محموله با شناسه
          "${parcelsList.find(item => item.id === id)?.id}"
          مطمئن هستید؟`}

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="cancelDescription"
                name="cancelDescription"
                label="علت لغو"
                error={inputError}
                onChange={handleCancelParcelReason}
              />
              {inputError && (
                <Typography
                  component="p"
                  align="right"
                  className={classes.errorText}
                  color="error"
                >
                  علت لغو الزامی میباشد.
                </Typography>
              )}
            </Grid>
          </div>
        </DialogTitle>
        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                autoFocus
                onClick={handleClose}
                variant="contained"
                color="secondary"
                size="large"
              >
                {'خیر'}
              </Button>
            </Grid>

            <Grid item xs={4}>
              <Button
                onClick={handleDisable}
                disabled={inputError}
                fullWidth
                variant="contained"
                size="large"
              >
                {'بله'}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        open={disableCancelParcelModal}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            <IconButton aria-label="close" onClick={handleDisableCancelModal}>
              <Icon path={mdiClose} size={1} />
            </IconButton>
          </div>
          <div className={classes.cancelIcon}>
            <Icon path={mdiClose} size={4} color={theme.palette.error.main} />
          </div>
          <div className={classes.cancelTitle}>
            {`
              امکان لغو محموله "${parcelsList.find(item => item.id === id)?.id
              }" در وضعیت فعلی وجود ندارد.
               
در صورت نیاز به اطلاعات بیشتر با پشتیبانی تماس حاصل فرمایید.              
            `}
          </div>
        </DialogTitle>
        <DialogActions>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Button
                fullWidth
                autoFocus
                onClick={handleDisableCancelModal}
                variant="contained"
                color="secondary"
                size="large"
              >
                {'بستن'}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        open={openConfirmUpdateState}
        onClose={() => setOpenConfirmUpdateState(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogContent>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            {handleClose ? (
              <IconButton
                aria-label="close"
                // className={classes.closeButton}
                onClick={() => setOpenConfirmUpdateState(false)}
              >
                <Icon path={mdiClose} size={1} />
              </IconButton>
            ) : null}
          </div>
          <div className={classes.editIcon}>
            <Icon
              path={mdiPencilOutline}
              size={4}
              color={theme.palette.blue.main}
            />
          </div>
          <div className={classes.cancelTitle}>
            {`از تغییر تگ وضعیت محموله
                با شناسه  ${id} از
 "${id > -1 && findStatus(id)}"
 به
" ${translate(previewState.replace(/"/g, '') as Status, 'parcel')}"

 
 مطمئن هستید؟`}
          </div>
        </DialogContent>
        <Divider />
        <DialogActions className={classes.actionsBox}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={2}>
              <Button
                fullWidth
                autoFocus
                onClick={() => setOpenConfirmUpdateState(false)}
                variant="outlined"
                size="large"
              >
                {'انصراف'}
              </Button>
            </Grid>
            <Grid item xs={5}>
              <Button
                fullWidth
                variant="contained"
                onClick={changeStatusParcel}
                size="large"
                color="primary"
              >
                {'تغییر وضعیت محموله'}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Parcels;
